/**
 * Display formatting.
 *
 * The server sends money as integers in the currency's minor unit. Conversion
 * to a decimal happens here and nowhere else, and only for display — no value
 * formatted by this module is ever sent back.
 */

const MINOR_UNIT_EXPONENT: Readonly<Record<string, number>> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

/**
 * How many minor units make one major unit, as a power of ten. Exported because
 * a form that accepts an amount in major units has to convert it back, and
 * getting that wrong is a hundredfold error in someone's money.
 */
export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENT[currency.toUpperCase()] ?? 2;
}

export function formatMoney(amountMinor: number, currency: string): string {
  const exponent = minorUnitExponent(currency);
  const major = amountMinor / 10 ** exponent;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(exponent)}`;
  }
}

/**
 * The per-unit rate a block price represents, e.g. 1 minor unit per block of 10
 * renders as "$0.001". Shown at higher precision than a currency normally uses,
 * because that is the whole point of block pricing.
 */
export function formatRate(unitPriceMinor: number, unitSize: number, currency: string): string {
  const exponent = minorUnitExponent(currency);
  const perUnit = unitPriceMinor / unitSize / 10 ** exponent;
  const digits = Math.max(exponent, Math.min(6, -Math.floor(Math.log10(perUnit || 1)) + 1));
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: digits,
    }).format(perUnit);
  } catch {
    return `${currency} ${perUnit.toFixed(digits)}`;
  }
}

export const formatNumber = (value: number): string => new Intl.NumberFormat().format(value);

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateRange(startIso: string, endIso: string): string {
  return `${formatDate(startIso)} – ${formatDate(endIso)}`;
}

/** "in 12 days" / "3 days ago", for period ends and invitation expiry. */
export function formatRelative(iso: string): string {
  const deltaMs = new Date(iso).getTime() - Date.now();
  const days = Math.round(deltaMs / 86_400_000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(days) < 1) {
    return formatter.format(Math.round(deltaMs / 3_600_000), 'hour');
  }
  return formatter.format(days, 'day');
}

/**
 * "1 user" / "3 users". Units come from the database, so this stays simple —
 * and knows to leave alone the ones that must not take an "s": symbols like
 * `GB`, and rates like `request/min`.
 */
export function pluralise(count: number, unit: string): string {
  if (count === 1 || unit.endsWith('s')) return unit;
  if (unit.includes('/')) return unit;
  if (unit === unit.toUpperCase()) return unit; // GB, TB, MB
  return `${unit}s`;
}

export function titleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
